import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import PremiumUpgradeModal from '@/components/PremiumUpgradeModal';

interface PremiumUpgradeContextType {
  showUpgradeModal: () => void;
  hideUpgradeModal: () => void;
  isModalOpen: boolean;
}

const PremiumUpgradeContext = createContext<PremiumUpgradeContextType | undefined>(undefined);

export const PremiumUpgradeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const showUpgradeModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const hideUpgradeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleUpgrade = useCallback(() => {
    hideUpgradeModal();
    navigate('/settings?section=premium', { replace: true });
  }, [navigate, hideUpgradeModal]);

  useEffect(() => {
    const handleShowModal = (event: Event) => {
      event.stopPropagation();
      if (!isModalOpen) {
        showUpgradeModal();
      }
    };

    window.addEventListener('show-premium-upgrade-modal', handleShowModal);
    
    return () => {
      window.removeEventListener('show-premium-upgrade-modal', handleShowModal);
    };
  }, [showUpgradeModal, isModalOpen]);

  return (
    <PremiumUpgradeContext.Provider value={{ showUpgradeModal, hideUpgradeModal, isModalOpen }}>
      {children}
      <PremiumUpgradeModal
        isOpen={isModalOpen}
        onClose={hideUpgradeModal}
        onUpgrade={handleUpgrade}
      />
    </PremiumUpgradeContext.Provider>
  );
};

export const usePremiumUpgrade = () => {
  const context = useContext(PremiumUpgradeContext);
  if (!context) {
    throw new Error('usePremiumUpgrade must be used within a PremiumUpgradeProvider');
  }
  return context;
}; 